import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Plus, Search, Filter, ChevronRight, Car, ArrowLeft, XCircle, CheckCircle2, Wrench,
  Warehouse, FileText, Clock, Cog, Package, Truck, ArrowLeftCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bucket definitions for Active Jobs
const ACTIVE_BUCKETS = [
  {
    id: 'in_garage',
    label: 'Cars in Garage',
    icon: Warehouse,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-400',
    statuses: ['reached_garage', 'ongoing', 'pending_estimate', 'draft', 'pickup_scheduled', 'picked_up', 'job_created', 'driver_assigned', 'received']
  },
  {
    id: 'quote_pending',
    label: 'Quote Pending',
    icon: FileText,
    color: 'from-yellow-500 to-amber-600',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    textColor: 'text-yellow-400',
    statuses: ['quote_raised', 'estimate_sent']
  },
  {
    id: 'approval_pending',
    label: 'Approval Pending',
    icon: Clock,
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    textColor: 'text-purple-400',
    statuses: ['estimate_sent', 'pending_approval']
  },
  {
    id: 'work_yet_to_start',
    label: 'Work Yet to Start',
    icon: Package,
    color: 'from-indigo-500 to-indigo-600',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/30',
    textColor: 'text-indigo-400',
    statuses: ['customer_approved', 'customer_partial_approved', 'quote_approved']
  },
  {
    id: 'work_in_progress',
    label: 'Work in Progress',
    icon: Cog,
    color: 'from-orange-500 to-red-600',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    textColor: 'text-orange-400',
    statuses: ['work_in_progress', 'qc', 'qc_pending', 'qc_passed', 'qc_failed']
  },
  {
    id: 'ready_for_delivery',
    label: 'Ready for Delivery',
    icon: Truck,
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-400',
    statuses: ['ready_for_delivery', 'drop_scheduled', 'invoiced']
  }
];

const JobsList = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('active');
  const [counts, setCounts] = useState({ active_count: 0, delivered_count: 0, cancelled_count: 0 });
  const [bucketCounts, setBucketCounts] = useState({});
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [showBucketView, setShowBucketView] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('garage_token');
    if (!token) {
      navigate('/garage/login');
      return;
    }
    fetchJobs();
    fetchCounts();
    if (activeTab === 'active') {
      fetchBucketCounts();
    }
  }, [navigate, statusFilter, sourceFilter, activeTab, selectedBucket]);

  const fetchBucketCounts = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/stats/bucket-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBucketCounts(data.buckets);
      }
    } catch (error) {
      console.error('Failed to fetch bucket counts');
    }
  };

  const fetchCounts = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/stats/counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCounts(data);
      }
    } catch (error) {
      console.error('Failed to fetch counts');
    }
  };

  const fetchJobs = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      let url = `${API_URL}/api/garage/jobs/?limit=50`;
      
      // Handle different tabs
      if (activeTab === 'cancelled') {
        url += `&status=cancelled&include_cancelled=true`;
      } else if (activeTab === 'delivered') {
        url += `&status=delivered&include_delivered=true`;
      } else {
        // Active tab - handle bucket filter or status filter
        if (selectedBucket) {
          const bucket = ACTIVE_BUCKETS.find(b => b.id === selectedBucket);
          if (bucket) {
            url += `&statuses=${bucket.statuses.join(',')}`;
          }
        } else if (statusFilter !== 'all') {
          url += `&status=${statusFilter}`;
        }
      }
      
      if (sourceFilter !== 'all') url += `&source=${sourceFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setJobs(data.jobs);
        setTotal(data.total);
      }
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchJobs();
  };

  const handleBucketClick = (bucketId) => {
    setSelectedBucket(bucketId);
    setShowBucketView(false);
    setLoading(true);
  };

  const handleBackToBuckets = () => {
    setSelectedBucket(null);
    setShowBucketView(true);
    setStatusFilter('all');
  };

  const getStatusColor = (status) => {
    const colors = {
      pickup_scheduled: 'bg-blue-400',
      picked_up: 'bg-blue-500',
      reached_garage: 'bg-indigo-500',
      ongoing: 'bg-purple-500',
      quote_raised: 'bg-yellow-500',
      quote_approved: 'bg-green-500',
      work_in_progress: 'bg-orange-500',
      ready_for_delivery: 'bg-cyan-500',
      drop_scheduled: 'bg-teal-500',
      dropped: 'bg-emerald-600',
      cancelled: 'bg-gray-500'
    };
    return colors[status] || 'bg-slate-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pickup_scheduled: 'Pickup Scheduled',
      picked_up: 'Picked Up',
      reached_garage: 'Reached Garage',
      ongoing: 'Ongoing',
      quote_raised: 'Quote Raised',
      estimate_sent: 'Estimate Sent',
      pending_approval: 'Pending Approval',
      quote_approved: 'Quote Approved',
      customer_approved: 'Approved',
      customer_partial_approved: 'Partial Approved',
      work_in_progress: 'Work in Progress',
      ready_for_delivery: 'Ready for Delivery',
      drop_scheduled: 'Drop Scheduled',
      dropped: 'Dropped',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  const getSelectedBucketInfo = () => {
    return ACTIVE_BUCKETS.find(b => b.id === selectedBucket);
  };

  return (
    <div className={`min-h-screen ${theme.pageBg} transition-colors duration-200 overflow-x-hidden`}>
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Page Title */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {selectedBucket && activeTab === 'active' ? (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBackToBuckets}
                className={theme.textSecondary}
              >
                <ArrowLeftCircle className="w-5 h-5 mr-1" />
                Back
              </Button>
            ) : null}
            <h1 className={`text-lg sm:text-xl font-bold ${theme.textPrimary} truncate`}>
              {selectedBucket ? getSelectedBucketInfo()?.label : 'All Jobs'}
            </h1>
          </div>
          <Button 
            onClick={() => navigate('/garage/jobs/new')}
            className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 flex-shrink-0"
            size="sm"
            data-testid="new-job-btn"
          >
            <Plus className="w-4 h-4 sm:mr-1" /> 
            <span className="hidden sm:inline">New Job</span>
          </Button>
        </div>

        {/* Active / Delivered / Cancelled Tabs */}
        <Tabs value={activeTab} onValueChange={(val) => {
          setActiveTab(val);
          setSelectedBucket(null);
          setShowBucketView(true);
        }} className="w-full">
          <TabsList className={`${theme.cardBg} border ${theme.border} p-1 grid grid-cols-3 w-full sm:w-auto sm:inline-flex`}>
            <TabsTrigger 
              value="active" 
              className={`data-[state=active]:bg-orange-500 data-[state=active]:text-white ${theme.textSecondary}`}
              data-testid="active-jobs-tab"
            >
              <Wrench className="w-4 h-4 mr-1 hidden sm:inline" />
              Active
              <Badge variant="outline" className="ml-1 sm:ml-2 text-xs">{counts.active_count}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="delivered" 
              className={`data-[state=active]:bg-green-500 data-[state=active]:text-white ${theme.textSecondary}`}
              data-testid="delivered-jobs-tab"
            >
              <CheckCircle2 className="w-4 h-4 mr-1 hidden sm:inline" />
              Delivered
              <Badge variant="outline" className="ml-1 sm:ml-2 text-xs">{counts.delivered_count}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="cancelled" 
              className={`data-[state=active]:bg-gray-500 data-[state=active]:text-white ${theme.textSecondary}`}
              data-testid="cancelled-jobs-tab"
            >
              <XCircle className="w-4 h-4 mr-1 hidden sm:inline" />
              Cancelled
              <Badge variant="outline" className="ml-1 sm:ml-2 text-xs">{counts.cancelled_count}</Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Bucket Summary View for Active Tab */}
        {activeTab === 'active' && showBucketView && !selectedBucket && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {ACTIVE_BUCKETS.map((bucket) => {
              const BucketIcon = bucket.icon;
              const count = bucketCounts[bucket.id] || 0;
              return (
                <Card 
                  key={bucket.id}
                  className={`${bucket.bgColor} ${bucket.borderColor} border cursor-pointer hover:scale-105 transition-all duration-200`}
                  onClick={() => handleBucketClick(bucket.id)}
                  data-testid={`bucket-${bucket.id}`}
                >
                  <CardContent className="p-4 text-center">
                    <div className={`w-12 h-12 mx-auto mb-2 rounded-full bg-gradient-to-br ${bucket.color} flex items-center justify-center`}>
                      <BucketIcon className="w-6 h-6 text-white" />
                    </div>
                    <p className={`text-2xl font-bold ${bucket.textColor}`}>{count}</p>
                    <p className={`text-xs ${theme.textSecondary} mt-1`}>{bucket.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Filters - show when bucket selected or not in bucket view */}
        {activeTab === 'active' && (selectedBucket || !showBucketView) && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by name, mobile, vehicle..."
                className={`pl-10 ${theme.inputBg} ${theme.textPrimary}`}
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className={`w-[110px] sm:w-[130px] flex-shrink-0 ${theme.inputBg} ${theme.textPrimary}`}>
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="propitstop">Propitstop</SelectItem>
                <SelectItem value="garage_own">Own</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} variant="outline" className={`${theme.border} ${theme.textPrimary} flex-shrink-0`}>
              <Filter className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Apply</span>
            </Button>
          </div>
        </div>
        )}

        {/* Search for Delivered/Cancelled tabs */}
        {(activeTab === 'delivered' || activeTab === 'cancelled') && (
          <div className="flex-1">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={`Search ${activeTab} jobs...`}
                className={`pl-10 ${theme.inputBg} ${theme.textPrimary}`}
              />
            </div>
          </div>
        )}

        {/* Jobs List - show when bucket is selected or not in bucket view */}
        {(activeTab !== 'active' || selectedBucket || !showBucketView) && (
        <>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
          </div>
        ) : jobs.length === 0 ? (
          <Card className={`${theme.cardBg} border`}>
            <CardContent className="py-12 text-center">
              <Car className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
              <h3 className={`text-lg font-medium ${theme.textPrimary} mb-2`}>
                {activeTab === 'cancelled' ? 'No Cancelled Jobs' : 
                 activeTab === 'delivered' ? 'No Delivered Jobs' : 'No Active Jobs'}
              </h3>
              <p className={`${theme.textSecondary} mb-4`}>
                {activeTab === 'cancelled' ? 'No jobs have been cancelled yet' : 
                 activeTab === 'delivered' ? 'No vehicles have been delivered yet' : 
                 'Create your first job to get started'}
              </p>
              {activeTab === 'active' && (
              <Button 
                onClick={() => navigate('/garage/jobs/new')}
                className="bg-gradient-to-r from-orange-500 to-red-600"
              >
                <Plus className="w-4 h-4 mr-1" /> Create Job
              </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {jobs.map((job) => (
              <Card 
                key={job.id}
                className={`${theme.cardBg} border ${theme.hoverBg} cursor-pointer transition-colors`}
                onClick={() => navigate(`/garage/jobs/${job.id}`)}
                data-testid={`job-card-${job.job_number}`}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start sm:items-center justify-between gap-2">
                    <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                      <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0 mt-1.5 sm:mt-0 ${getStatusColor(job.status)}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-bold text-sm sm:text-base ${theme.textPrimary}`}>{job.job_number}</span>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${job.source === 'propitstop' ? 'border-orange-500 text-orange-400' : `${theme.border} ${theme.textSecondary}`}`}
                          >
                            {job.source === 'propitstop' ? 'Propitstop' : 'Own'}
                          </Badge>
                        </div>
                        <div className={`text-xs sm:text-sm ${theme.textSecondary} truncate`}>
                          {job.customer_name} • {job.vehicle_number || 'No plate'} {job.vehicle_make && `• ${job.vehicle_make}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge className={`${getStatusColor(job.status)} text-white text-xs whitespace-nowrap`}>
                        {getStatusLabel(job.status)}
                      </Badge>
                      <ChevronRight className={`w-4 h-4 sm:w-5 sm:h-5 ${theme.textMuted} hidden sm:block`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
};

export default JobsList;
